export const COMPANION_MESSAGE_MAX_CODE_POINTS = 32_768;
export const COMPANION_REQUEST_ID_MAX_LENGTH = 128;
export const COMPANION_AGENT_NAME_MAX_LENGTH = 256;
export const COMPANION_DESKTOP_AFTER_MAX_LENGTH = 256;
export const COMPANION_BRIDGE_LINE_MAX_LENGTH = 140_000;
export const COMPANION_HISTORY_LIMIT_MAX = 40;
export const COMPANION_TURN_TEXT_MAX_CODE_POINTS = 32_768;
export const COMPANION_REPLY_DELTA_MAX_CODE_POINTS = 4_096;
export const COMPANION_TURN_ID_MAX_LENGTH = 256;
export const COMPANION_DECISION_CALL_ID_MAX_LENGTH = 256;
export const COMPANION_DECISION_TITLE_MAX_LENGTH = 200;
export const COMPANION_DECISION_LABEL_MAX_LENGTH = 100;
export const COMPANION_DECISION_DETAIL_MAX_CODE_POINTS = 400;
export const COMPANION_DECISION_OPTIONS_MIN = 2;
export const COMPANION_DECISION_OPTIONS_MAX = 4;
export const COMPANION_ARTIFACT_NAME_MAX_LENGTH = 200;
export const COMPANION_ARTIFACTS_MAX = 16;

export type PersonaKey = "startup-advisor" | "ezra";

export interface CompanionDecisionOption {
  readonly label: string;
  readonly detail: string;
}

/**
 * A decision the mate has put to the user: a question with 2-4 concrete
 * options. `pending` is answerable; `selected` names the chosen option; a
 * `dismissed` card was closed by the conversation moving on without a choice.
 */
export type CompanionDecision = {
  readonly callId: string;
  readonly title: string;
  readonly options: readonly CompanionDecisionOption[];
} & (
  | { readonly status: "pending" | "dismissed" }
  | { readonly status: "selected"; readonly optionIndex: number }
);

/** One file the mate produced alongside a reply, named for a chip. */
export interface CompanionArtifact {
  readonly name: string;
  readonly sizeBytes?: number;
}

/** One rendered message of a mate's canonical conversation. */
export interface CompanionTurn {
  readonly id: string;
  readonly role: "user" | "mate";
  readonly text: string;
  readonly at: string | null;
  /**
   * A decision raised at this point of the conversation, or the files this
   * turn produced. A turn may carry either with no text of its own, so
   * `text` may be empty exactly when one of them is present.
   */
  readonly decision?: CompanionDecision;
  readonly artifacts?: readonly CompanionArtifact[];
}

export interface CompanionMate {
  readonly personaKey: PersonaKey;
  readonly agentName: string;
  readonly displayName: "Stacey" | "Ezra";
  readonly portraitKey: "stacey" | "ezra";
  readonly activeSince: string;
}

export type CompanionBridgeRequest =
  | { readonly type: "roster" }
  | { readonly type: "update" }
  | {
      readonly type: "send";
      readonly requestId: string;
      readonly personaKey: PersonaKey;
      readonly draftRevision: number;
      readonly text: string;
    }
  /**
   * `send`, plus the mate's reply streamed back as reply-delta events. A
   * separate type — never a flag on `send` — so a helper that predates it
   * rejects the whole request cleanly instead of half-understanding it, and an
   * app that predates it can never receive events it cannot parse.
   */
  | {
      readonly type: "converse";
      readonly requestId: string;
      readonly personaKey: PersonaKey;
      readonly draftRevision: number;
      readonly text: string;
    }
  /**
   * `converse`, with the reply's decisions and artifacts carried as their own
   * events instead of dropped. A separate type for the same reason `converse`
   * is: an app only ever receives reply-decision or reply-artifacts events on
   * an exchange it opened with `chat`, so an app that predates them can never
   * be handed an event it cannot parse, and a helper that predates them
   * rejects the whole request cleanly.
   */
  | {
      readonly type: "chat";
      readonly requestId: string;
      readonly personaKey: PersonaKey;
      readonly draftRevision: number;
      readonly text: string;
    }
  /**
   * Answer a pending decision with the option at `optionIndex`. The reply
   * streams back like `chat`: the settled card first, then the mate's
   * follow-up text.
   */
  | {
      readonly type: "decide";
      readonly requestId: string;
      readonly personaKey: PersonaKey;
      readonly callId: string;
      readonly optionIndex: number;
    }
  | {
      readonly type: "history";
      readonly requestId: string;
      readonly personaKey: PersonaKey;
      readonly limit: number;
    }
  /**
   * `history`, with decision turns included. Separate from `history` exactly
   * as `chat` is from `converse`: only a `recall` exchange may carry turns
   * with a decision.
   */
  | {
      readonly type: "recall";
      readonly requestId: string;
      readonly personaKey: PersonaKey;
      readonly limit: number;
    };

export type CompanionBridgeEvent =
  /**
   * What the release channel offers against what is installed. The app has no
   * way to reach the channel itself — no network of any kind — so this is the
   * only way it can know it is out of date.
   */
  | {
      readonly type: "update";
      readonly installed: string | null;
      readonly available: string | null;
      readonly severity: "critical" | "recommended" | "optional" | null;
    }
  | {
      readonly type: "roster";
      readonly origin: string;
      readonly mates: readonly CompanionMate[];
    }
  | {
      readonly type: "dispatching";
      readonly requestId: string;
      readonly personaKey: PersonaKey;
      readonly route: string;
    }
  | {
      readonly type: "accepted";
      readonly requestId: string;
      readonly personaKey: PersonaKey;
      readonly route: string;
    }
  | {
      readonly type: "completed";
      readonly requestId: string;
    }
  | {
      readonly type: "turn";
      readonly requestId: string;
      readonly turn: CompanionTurn;
    }
  | {
      readonly type: "history-end";
      readonly requestId: string;
      readonly count: number;
    }
  | {
      readonly type: "reply-delta";
      readonly requestId: string;
      readonly text: string;
    }
  /**
   * A decision the reply carries, or a state change to one already shown. A
   * decision turn carries NO text, so an exchange that drops this event
   * renders as pure silence. Only `chat` and `decide` exchanges emit it.
   */
  | {
      readonly type: "reply-decision";
      readonly requestId: string;
      readonly decision: CompanionDecision;
    }
  /** Files the reply produced, named for chips. Only on `chat` and `decide`. */
  | {
      readonly type: "reply-artifacts";
      readonly requestId: string;
      readonly artifacts: readonly CompanionArtifact[];
    }
  | {
      readonly type: "indeterminate";
      readonly requestId: string;
      readonly stage: "dispatch" | "after-accept";
      readonly route: string;
    }
  | {
      readonly type: "failed";
      readonly requestId?: string;
      readonly reason:
        | "not-connected"
        | "not-authorized"
        | "mate-unavailable"
        /**
         * Two deployed agents claim the same persona, so there is no answer to
         * "which one would this reach". Its own reason, never folded into
         * `mate-unavailable`: absence and ambiguity have opposite remedies —
         * one is "deploy a mate", the other is "remove one" — and a desktop
         * that cannot tell them apart tells the founder the wrong thing.
         */
        | "mate-ambiguous"
        | "busy"
        | "bad-request"
        /**
         * The decision is no longer awaiting an answer — settled from another
         * surface, or the conversation moved past it. Only a `decide`
         * exchange fails with this.
         */
        | "decision-not-pending";
    };

const PERSONAS = new Set<PersonaKey>([
  "startup-advisor",
  "ezra",
]);
const FAILURE_REASONS = new Set([
  "not-connected",
  "not-authorized",
  "mate-unavailable",
  "mate-ambiguous",
  "busy",
  "bad-request",
  "decision-not-pending",
]);
const CONTROL_CHARACTERS = /[\u0000-\u001f\u007f]/u;
const URL_SAFE_OPAQUE_ID = /^[A-Za-z0-9_-]+$/u;
const SEVERITIES = new Set(["critical", "recommended", "optional"]);
/** Bounded and printable: a version reaches a window, and nothing else does. */
const VERSION = /^[A-Za-z0-9][A-Za-z0-9._+-]{0,63}$/u;

function isVersionOrNull(value: unknown): boolean {
  return value === null || (typeof value === "string" && VERSION.test(value));
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return (
    typeof value === "object" && value !== null && !Array.isArray(value)
  );
}

function hasExactKeys(
  value: Record<string, unknown>,
  keys: readonly string[],
): boolean {
  const actual = Object.keys(value);
  return (
    actual.length === keys.length &&
    keys.every((key) => Object.hasOwn(value, key))
  );
}

function isPersonaKey(value: unknown): value is PersonaKey {
  return typeof value === "string" && PERSONAS.has(value as PersonaKey);
}

function isBoundedPlainString(
  value: unknown,
  maxLength: number,
): value is string {
  return (
    typeof value === "string" &&
    value.length > 0 &&
    value.length <= maxLength &&
    !CONTROL_CHARACTERS.test(value)
  );
}

function isRequestId(value: unknown): value is string {
  return isBoundedPlainString(value, COMPANION_REQUEST_ID_MAX_LENGTH);
}

/**
 * Message text may span lines and be indented; every other control character
 * is refused. Distinct from the single-line rule that guards labels and ids.
 */
const FORBIDDEN_TEXT_CHARACTERS =
  /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/u;

function isBoundedMessageText(
  value: unknown,
  maxCodePoints: number,
): value is string {
  return (
    typeof value === "string" &&
    value.length > 0 &&
    !FORBIDDEN_TEXT_CHARACTERS.test(value) &&
    [...value].length <= maxCodePoints
  );
}

/**
 * A parseable ISO-8601 UTC instant, or null. Looser than the roster's rule on
 * purpose: a message stamped moments ago on a skewed server clock must not
 * invalidate a whole transcript.
 */
const INSTANT_SHAPE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?Z$/;

function isInstantOrNull(value: unknown): boolean {
  return (
    value === null ||
    (typeof value === "string" &&
      INSTANT_SHAPE.test(value) &&
      Number.isFinite(Date.parse(value)))
  );
}

function parseDecision(value: unknown): CompanionDecision {
  if (
    !isRecord(value) ||
    !isBoundedPlainString(value.callId, COMPANION_DECISION_CALL_ID_MAX_LENGTH) ||
    !isBoundedPlainString(value.title, COMPANION_DECISION_TITLE_MAX_LENGTH) ||
    !Array.isArray(value.options) ||
    value.options.length < COMPANION_DECISION_OPTIONS_MIN ||
    value.options.length > COMPANION_DECISION_OPTIONS_MAX
  ) {
    return eventError();
  }
  const options = value.options.map((option): CompanionDecisionOption => {
    if (
      !isRecord(option) ||
      !hasExactKeys(option, ["label", "detail"]) ||
      !isBoundedPlainString(option.label, COMPANION_DECISION_LABEL_MAX_LENGTH) ||
      !isBoundedMessageText(option.detail, COMPANION_DECISION_DETAIL_MAX_CODE_POINTS)
    ) {
      return eventError();
    }
    return { label: option.label, detail: option.detail };
  });
  const base = { callId: value.callId, title: value.title, options };
  if (value.status === "pending" || value.status === "dismissed") {
    if (!hasExactKeys(value, ["callId", "title", "options", "status"])) {
      return eventError();
    }
    return { ...base, status: value.status };
  }
  if (
    value.status !== "selected" ||
    !hasExactKeys(value, ["callId", "title", "options", "status", "optionIndex"]) ||
    !Number.isSafeInteger(value.optionIndex) ||
    (value.optionIndex as number) < 0 ||
    (value.optionIndex as number) >= options.length
  ) {
    return eventError();
  }
  return { ...base, status: "selected", optionIndex: value.optionIndex as number };
}

function parseArtifact(value: unknown): CompanionArtifact {
  if (
    !isRecord(value) ||
    !isBoundedPlainString(value.name, COMPANION_ARTIFACT_NAME_MAX_LENGTH)
  ) {
    return eventError();
  }
  if (value.sizeBytes === undefined) {
    if (!hasExactKeys(value, ["name"])) return eventError();
    return { name: value.name };
  }
  if (
    !hasExactKeys(value, ["name", "sizeBytes"]) ||
    !Number.isSafeInteger(value.sizeBytes) ||
    (value.sizeBytes as number) < 0
  ) {
    return eventError();
  }
  return { name: value.name, sizeBytes: value.sizeBytes as number };
}

function parseTurnArtifacts(value: unknown): readonly CompanionArtifact[] {
  if (
    !Array.isArray(value) ||
    value.length === 0 ||
    value.length > COMPANION_ARTIFACTS_MAX
  ) {
    return eventError();
  }
  return value.map(parseArtifact);
}

function parseTurn(value: unknown): CompanionTurn {
  if (
    !isRecord(value) ||
    !isBoundedPlainString(value.id, COMPANION_TURN_ID_MAX_LENGTH) ||
    (value.role !== "user" && value.role !== "mate") ||
    !isInstantOrNull(value.at)
  ) {
    return eventError();
  }
  const keys = [
    "id",
    "role",
    "text",
    "at",
    ...(value.decision === undefined ? [] : ["decision"]),
    ...(value.artifacts === undefined ? [] : ["artifacts"]),
  ];
  if (!hasExactKeys(value, keys)) return eventError();
  // A turn carrying a decision or files may say nothing of its own; any text
  // it does carry is held to the same rule as every other turn.
  const allowEmptyText =
    value.decision !== undefined || value.artifacts !== undefined;
  if (
    !(
      (allowEmptyText && value.text === "") ||
      isBoundedMessageText(value.text, COMPANION_TURN_TEXT_MAX_CODE_POINTS)
    )
  ) {
    return eventError();
  }
  return {
    id: value.id,
    role: value.role,
    text: value.text as string,
    at: value.at as string | null,
    ...(value.decision === undefined
      ? {}
      : { decision: parseDecision(value.decision) }),
    ...(value.artifacts === undefined
      ? {}
      : { artifacts: parseTurnArtifacts(value.artifacts) }),
  };
}

function isCanonicalInstant(value: unknown): value is string {
  if (typeof value !== "string") return false;
  const time = Date.parse(value);
  return (
    Number.isFinite(time) &&
    new Date(time).toISOString() === value &&
    time <= Date.now()
  );
}

function requestError(): never {
  throw new Error("Invalid companion bridge request");
}

function eventError(): never {
  throw new Error("Invalid companion bridge event");
}

export function isGatewayOrigin(value: unknown): value is string {
  if (typeof value !== "string" || !value.startsWith("https://")) {
    return false;
  }
  let parsed: URL;
  try {
    parsed = new URL(value);
  } catch {
    return false;
  }
  return (
    parsed.origin === value &&
    parsed.protocol === "https:" &&
    parsed.username === "" &&
    parsed.password === "" &&
    parsed.pathname === "/" &&
    parsed.search === "" &&
    parsed.hash === ""
  );
}

export function isValidMateRoute(value: unknown): value is string {
  if (
    typeof value !== "string" ||
    !value.startsWith("/") ||
    value.startsWith("//") ||
    value.includes("#")
  ) {
    return false;
  }

  try {
    const parsed = new URL(value, "https://companion.invalid");
    if (
      parsed.origin !== "https://companion.invalid" ||
      !/^\/mates\/(?:startup-advisor|ezra)$/u.test(parsed.pathname)
    ) {
      return false;
    }

    const entries = [...parsed.searchParams.entries()];
    const keys = entries.map(([key]) => key);
    if (
      new Set(keys).size !== keys.length ||
      keys.some(
        (key) => key !== "desktopPending" && key !== "desktopAfter",
      )
    ) {
      return false;
    }

    if (
      parsed.searchParams.has("desktopPending") &&
      parsed.searchParams.get("desktopPending") !== "1"
    ) {
      return false;
    }

    if (parsed.searchParams.has("desktopAfter")) {
      const after = parsed.searchParams.get("desktopAfter");
      if (
        after === null ||
        after.length === 0 ||
        after.length > COMPANION_DESKTOP_AFTER_MAX_LENGTH ||
        !URL_SAFE_OPAQUE_ID.test(after)
      ) {
        return false;
      }
    }

    return true;
  } catch {
    return false;
  }
}

export function parseRequest(value: unknown): CompanionBridgeRequest {
  if (!isRecord(value)) return requestError();

  if (value.type === "roster") {
    if (!hasExactKeys(value, ["type"])) return requestError();
    return { type: "roster" };
  }

  if (value.type === "update") {
    if (!hasExactKeys(value, ["type"])) return requestError();
    return { type: "update" };
  }

  if (value.type === "history" || value.type === "recall") {
    if (
      !hasExactKeys(value, ["type", "requestId", "personaKey", "limit"]) ||
      !isRequestId(value.requestId) ||
      !isPersonaKey(value.personaKey) ||
      !Number.isSafeInteger(value.limit) ||
      (value.limit as number) < 1 ||
      (value.limit as number) > COMPANION_HISTORY_LIMIT_MAX
    ) {
      return requestError();
    }
    return {
      type: value.type,
      requestId: value.requestId,
      personaKey: value.personaKey,
      limit: value.limit as number,
    };
  }

  if (value.type === "decide") {
    if (
      !hasExactKeys(value, [
        "type",
        "requestId",
        "personaKey",
        "callId",
        "optionIndex",
      ]) ||
      !isRequestId(value.requestId) ||
      !isPersonaKey(value.personaKey) ||
      !isBoundedPlainString(value.callId, COMPANION_DECISION_CALL_ID_MAX_LENGTH) ||
      !Number.isSafeInteger(value.optionIndex) ||
      (value.optionIndex as number) < 0 ||
      (value.optionIndex as number) >= COMPANION_DECISION_OPTIONS_MAX
    ) {
      return requestError();
    }
    return {
      type: "decide",
      requestId: value.requestId,
      personaKey: value.personaKey,
      callId: value.callId,
      optionIndex: value.optionIndex as number,
    };
  }

  if (
    (value.type !== "send" && value.type !== "converse" && value.type !== "chat") ||
    !hasExactKeys(value, [
      "type",
      "requestId",
      "personaKey",
      "draftRevision",
      "text",
    ]) ||
    !isRequestId(value.requestId) ||
    !isPersonaKey(value.personaKey) ||
    !Number.isSafeInteger(value.draftRevision) ||
    (value.draftRevision as number) < 0 ||
    typeof value.text !== "string" ||
    value.text.trim().length === 0 ||
    [...value.text].length > COMPANION_MESSAGE_MAX_CODE_POINTS
  ) {
    return requestError();
  }

  return {
    type: value.type,
    requestId: value.requestId,
    personaKey: value.personaKey,
    draftRevision: value.draftRevision as number,
    text: value.text,
  };
}

export function serializeRequest(value: CompanionBridgeRequest): string {
  return JSON.stringify(parseRequest(value));
}

export function parseRequestLine(line: string): CompanionBridgeRequest {
  if (
    typeof line !== "string" ||
    line.length > COMPANION_BRIDGE_LINE_MAX_LENGTH ||
    /[\r\n]/u.test(line)
  ) {
    return requestError();
  }

  try {
    return parseRequest(JSON.parse(line));
  } catch {
    return requestError();
  }
}

function parseMate(value: unknown): CompanionMate {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, [
      "personaKey",
      "agentName",
      "displayName",
      "portraitKey",
      "activeSince",
    ]) ||
    !isPersonaKey(value.personaKey) ||
    !isBoundedPlainString(
      value.agentName,
      COMPANION_AGENT_NAME_MAX_LENGTH,
    ) ||
    !isCanonicalInstant(value.activeSince)
  ) {
    return eventError();
  }

  const identity =
    value.personaKey === "startup-advisor"
      ? { displayName: "Stacey" as const, portraitKey: "stacey" as const }
      : { displayName: "Ezra" as const, portraitKey: "ezra" as const };
  if (
    value.displayName !== identity.displayName ||
    value.portraitKey !== identity.portraitKey
  ) {
    return eventError();
  }

  return {
    personaKey: value.personaKey,
    agentName: value.agentName,
    displayName: identity.displayName,
    portraitKey: identity.portraitKey,
    activeSince: value.activeSince,
  };
}

export function parseEvent(value: unknown): CompanionBridgeEvent {
  if (!isRecord(value)) return eventError();

  if (value.type === "update") {
    if (
      !hasExactKeys(value, ["type", "installed", "available", "severity"]) ||
      !isVersionOrNull(value.installed) ||
      !isVersionOrNull(value.available) ||
      !(value.severity === null || SEVERITIES.has(value.severity as string))
    ) {
      return eventError();
    }
    return {
      type: "update",
      installed: value.installed as string | null,
      available: value.available as string | null,
      severity: value.severity as "critical" | "recommended" | "optional" | null,
    };
  }

  if (value.type === "roster") {
    // The roster reports WHO IS DEPLOYED, which is not a fixed pair: an Ezra-only
    // install reports one mate. This used to demand exactly two, so that roster was
    // refused on the wire and the desktop fell through to "not connected" — every
    // head greyed, "Reconnecting…", forever, on a platform that was perfectly
    // healthy. At least one, and no ambiguity, is the real rule; the count never was.
    if (
      !hasExactKeys(value, ["type", "origin", "mates"]) ||
      !isGatewayOrigin(value.origin) ||
      !Array.isArray(value.mates) ||
      value.mates.length === 0
    ) {
      return eventError();
    }
    const mates = value.mates.map(parseMate);
    if (
      new Set(mates.map(({ personaKey }) => personaKey)).size !== mates.length ||
      new Set(mates.map(({ agentName }) => agentName)).size !== mates.length
    ) {
      return eventError();
    }
    return { type: "roster", origin: value.origin, mates };
  }

  if (value.type === "failed") {
    const keys =
      value.requestId === undefined
        ? ["type", "reason"]
        : ["type", "requestId", "reason"];
    if (
      !hasExactKeys(value, keys) ||
      (value.requestId !== undefined && !isRequestId(value.requestId)) ||
      typeof value.reason !== "string" ||
      !FAILURE_REASONS.has(value.reason)
    ) {
      return eventError();
    }
    return value.requestId === undefined
      ? {
          type: "failed",
          reason: value.reason as Extract<
            CompanionBridgeEvent,
            { type: "failed" }
          >["reason"],
        }
      : {
          type: "failed",
          requestId: value.requestId,
          reason: value.reason as Extract<
            CompanionBridgeEvent,
            { type: "failed" }
          >["reason"],
        };
  }

  if (value.type === "completed") {
    if (
      !hasExactKeys(value, ["type", "requestId"]) ||
      !isRequestId(value.requestId)
    ) {
      return eventError();
    }
    return { type: "completed", requestId: value.requestId };
  }

  if (value.type === "turn") {
    if (
      !hasExactKeys(value, ["type", "requestId", "turn"]) ||
      !isRequestId(value.requestId)
    ) {
      return eventError();
    }
    return {
      type: "turn",
      requestId: value.requestId,
      turn: parseTurn(value.turn),
    };
  }

  if (value.type === "reply-decision") {
    if (
      !hasExactKeys(value, ["type", "requestId", "decision"]) ||
      !isRequestId(value.requestId)
    ) {
      return eventError();
    }
    return {
      type: "reply-decision",
      requestId: value.requestId,
      decision: parseDecision(value.decision),
    };
  }

  if (value.type === "reply-artifacts") {
    if (
      !hasExactKeys(value, ["type", "requestId", "artifacts"]) ||
      !isRequestId(value.requestId) ||
      !Array.isArray(value.artifacts) ||
      value.artifacts.length === 0 ||
      value.artifacts.length > COMPANION_ARTIFACTS_MAX
    ) {
      return eventError();
    }
    return {
      type: "reply-artifacts",
      requestId: value.requestId,
      artifacts: value.artifacts.map(parseArtifact),
    };
  }

  if (value.type === "history-end") {
    if (
      !hasExactKeys(value, ["type", "requestId", "count"]) ||
      !isRequestId(value.requestId) ||
      !Number.isSafeInteger(value.count) ||
      (value.count as number) < 0 ||
      (value.count as number) > COMPANION_HISTORY_LIMIT_MAX
    ) {
      return eventError();
    }
    return {
      type: "history-end",
      requestId: value.requestId,
      count: value.count as number,
    };
  }

  if (value.type === "reply-delta") {
    if (
      !hasExactKeys(value, ["type", "requestId", "text"]) ||
      !isRequestId(value.requestId) ||
      !isBoundedMessageText(value.text, COMPANION_REPLY_DELTA_MAX_CODE_POINTS)
    ) {
      return eventError();
    }
    return {
      type: "reply-delta",
      requestId: value.requestId,
      text: value.text,
    };
  }

  if (value.type === "dispatching" || value.type === "accepted") {
    if (
      !hasExactKeys(value, [
        "type",
        "requestId",
        "personaKey",
        "route",
      ]) ||
      !isRequestId(value.requestId) ||
      !isPersonaKey(value.personaKey) ||
      !isValidMateRoute(value.route) ||
      !value.route.startsWith(`/mates/${value.personaKey}`)
    ) {
      return eventError();
    }
    return {
      type: value.type,
      requestId: value.requestId,
      personaKey: value.personaKey,
      route: value.route,
    };
  }

  if (value.type === "indeterminate") {
    if (
      !hasExactKeys(value, ["type", "requestId", "stage", "route"]) ||
      !isRequestId(value.requestId) ||
      (value.stage !== "dispatch" && value.stage !== "after-accept") ||
      !isValidMateRoute(value.route)
    ) {
      return eventError();
    }
    return {
      type: "indeterminate",
      requestId: value.requestId,
      stage: value.stage,
      route: value.route,
    };
  }

  return eventError();
}

export function serializeEvent(value: CompanionBridgeEvent): string {
  return JSON.stringify(parseEvent(value));
}

export function parseEventLine(line: string): CompanionBridgeEvent {
  if (
    typeof line !== "string" ||
    line.length > COMPANION_BRIDGE_LINE_MAX_LENGTH ||
    /[\r\n]/u.test(line)
  ) {
    return eventError();
  }

  try {
    return parseEvent(JSON.parse(line));
  } catch {
    return eventError();
  }
}

export function validateEventSequence(
  events: readonly CompanionBridgeEvent[],
): boolean {
  let parsed: CompanionBridgeEvent[];
  try {
    parsed = events.map(parseEvent);
  } catch {
    return false;
  }
  if (parsed.length === 0) return false;

  const first = parsed[0];
  if (first.type === "roster" || first.type === "failed") {
    return parsed.length === 1;
  }

  if (first.type === "turn" || first.type === "history-end") {
    const last = parsed.at(-1);
    if (last?.type !== "history-end") return false;
    const turns = parsed.slice(0, -1);
    return (
      last.count === turns.length &&
      turns.every(
        (event) =>
          event.type === "turn" && event.requestId === last.requestId,
      )
    );
  }

  if (first.type !== "dispatching") return false;

  if (parsed.length === 2 && parsed[1]?.type === "indeterminate") {
    const terminal = parsed[1];
    return (
      terminal.requestId === first.requestId &&
      terminal.stage === "dispatch" &&
      terminal.route === first.route
    );
  }

  // A dispatch the gateway refused outright with a nameable reason — a
  // `decide` on a card that is no longer pending — closes as a failure
  // rather than an indeterminate outcome.
  if (parsed.length === 2 && parsed[1]?.type === "failed") {
    return parsed[1].requestId === first.requestId;
  }

  if (parsed.length < 3 || parsed[1]?.type !== "accepted") return false;
  const accepted = parsed[1];
  if (
    accepted.requestId !== first.requestId ||
    accepted.personaKey !== first.personaKey ||
    accepted.route !== first.route
  ) {
    return false;
  }

  const deltas = parsed.slice(2, -1);
  if (
    !deltas.every(
      (event) =>
        (event.type === "reply-delta" ||
          event.type === "reply-decision" ||
          event.type === "reply-artifacts") &&
        event.requestId === first.requestId,
    )
  ) {
    return false;
  }

  const terminal = parsed.at(-1);
  if (
    terminal?.type === "completed" &&
    terminal.requestId === first.requestId
  ) {
    return true;
  }
  return (
    terminal?.type === "indeterminate" &&
    terminal.requestId === first.requestId &&
    terminal.stage === "after-accept" &&
    terminal.route === first.route
  );
}
